import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Form,
  Icon,
  Label,
  Loader,
  Message,
  Modal,
  Popup,
  Progress,
  Segment,
  Table,
} from 'semantic-ui-react';
import { stockyApi } from '../misc/StockyApi';
import images from '../../images';
import { surfaceCardStyle } from '../misc/pageStyles';
import {
  analyzeSelectedSkus,
  buildTiendaNubeOptionsPayload,
  countCopyUnits,
  createPublishJob,
  groupDestinationAccounts,
  groupListingsBySourceProduct,
  getPublicationStatusMeta,
  getPublishJobStatus,
  retryPublicationInJob,
  resolvePublicationLabel,
} from './publishInAnotherAccountWorkflow';

const STEP_ANALYZING = 0;
const STEP_PREVIEW = 1;
const STEP_DESTINATION = 2;
const STEP_OPTIONS = 3;
const STEP_CONFIRM = 4;
const STEP_JOB = 5;

const initialOptions = {
  copyPrice: true,
  copyImages: true,
  copyDescription: true,
  copyAttributes: true,
  copyVariants: true,
  publishState: 'published',
  priceAdjustmentPercent: 0,
  copyPromotionalPrice: true,
  appendSpecsToDescription: true,
  copyFreeShipping: false,
  stockSource: 'mercury',
};

const channelTabs = [
  { key: 'mercado_libre', label: 'Mercado Libre' },
  { key: 'tienda_nube', label: 'Tienda Nube' },
];

function channelIcon(channel) {
  return channel === 'tienda_nube' ? images.tnIcon : images.meliIcon;
}

function statusTone(status) {
  const meta = getPublicationStatusMeta(status);
  return meta?.color || 'grey';
}

function stepTitle(step) {
  if (step === STEP_ANALYZING) return 'Analizando';
  if (step === STEP_PREVIEW) return 'Preview de listings';
  if (step === STEP_DESTINATION) return 'Elegir destino';
  if (step === STEP_OPTIONS) return 'Opciones';
  if (step === STEP_CONFIRM) return 'Confirmar';
  return 'Job iniciado';
}

function PublishInAnotherAccountDrawer({
  open,
  onClose,
  authUser,
  selectedRows = [],
  resumeJobId = null,
  jobSnapshot = null,
  onJobCreated,
  onJobSnapshotChange,
  publishInAnotherAccountEnabled = false,
  publishInAnotherAccountCopiesUsed = 0,
  publishInAnotherAccountFreeLimit = 5,
  preloadedDestinationAccounts = null,
  preloadedDestinationAccountsLoading = false,
}) {
  const [step, setStep] = useState(STEP_ANALYZING);
  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState('');
  const [destinationAccounts, setDestinationAccounts] = useState([]);
  const [destinationLoading, setDestinationLoading] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState('mercado_libre');
  const [selectedDestinationByChannel, setSelectedDestinationByChannel] = useState({
    mercado_libre: '',
    tienda_nube: '',
  });
  const [selectedListingIds, setSelectedListingIds] = useState([]);
  const [options, setOptions] = useState(initialOptions);
  const [jobDetailOpen, setJobDetailOpen] = useState(false);
  const [jobError, setJobError] = useState('');
  const [correctionModalOpen, setCorrectionModalOpen] = useState(false);
  const [correctionTarget, setCorrectionTarget] = useState(null);
  const [correctionTitle, setCorrectionTitle] = useState('');
  const [correctionSaving, setCorrectionSaving] = useState(false);
  const [correctionError, setCorrectionError] = useState('');
  const [copyPreview, setCopyPreview] = useState(null);
  const [copyPreviewLoading, setCopyPreviewLoading] = useState(false);
  const [copyPreviewError, setCopyPreviewError] = useState('');

  const selectedSkuIds = useMemo(
    () => (Array.isArray(selectedRows) ? selectedRows.map((row) => String(row?.id || '').trim()).filter(Boolean) : []),
    [selectedRows],
  );

  const currentDestinationAccounts = useMemo(
    () => groupDestinationAccounts(destinationAccounts, selectedChannel),
    [destinationAccounts, selectedChannel],
  );

  const previewListings = useMemo(
    () => (Array.isArray(analysis?.previewListings) ? analysis.previewListings : []),
    [analysis],
  );
  const selectedPreviewListings = useMemo(() => {
    const selectedIds = new Set(selectedListingIds);
    return previewListings.filter((listing) => selectedIds.has(listing.listingId));
  }, [previewListings, selectedListingIds]);
  const selectedDestinationAccount = useMemo(
    () => currentDestinationAccounts.find((account) => account.id === selectedDestinationByChannel[selectedChannel]) || null,
    [currentDestinationAccounts, selectedDestinationByChannel, selectedChannel],
  );
  const selectedDestinationCount = selectedDestinationAccount ? 1 : 0;
  const copyCount = useMemo(() => countCopyUnits(selectedPreviewListings), [selectedPreviewListings]);
  const totalToCreate = copyCount * selectedDestinationCount;
  const isTiendaNubeDestination = selectedChannel === 'tienda_nube';
  const blockedProductKeys = useMemo(
    () =>
      new Set(
        (copyPreview?.products || [])
          .filter((product) => Array.isArray(product?.blocking) && product.blocking.length > 0)
          .map((product) => String(product?.sourceRef?.productReference || '')),
      ),
    [copyPreview],
  );
  const copyableListings = useMemo(() => {
    if (!isTiendaNubeDestination || blockedProductKeys.size === 0) {
      return selectedPreviewListings;
    }
    return selectedPreviewListings.filter(
      (listing) =>
        !blockedProductKeys.has(String(listing.marketplaceId || '').split('-')[0]),
    );
  }, [isTiendaNubeDestination, blockedProductKeys, selectedPreviewListings]);
  const copyableCount = useMemo(() => countCopyUnits(copyableListings), [copyableListings]);
  const quotaUsed = Number(publishInAnotherAccountCopiesUsed || 0);
  const quotaLimit = Number(publishInAnotherAccountFreeLimit || 0);
  const quotaRemaining = quotaLimit > 0 ? Math.max(0, quotaLimit - quotaUsed) : 0;
  const quotaBlocked =
    publishInAnotherAccountEnabled !== true ||
    (quotaLimit > 0 && quotaRemaining <= 0) ||
    (quotaLimit > 0 && totalToCreate > quotaRemaining);
  const quotaBlockingMessage =
    publishInAnotherAccountEnabled !== true
      ? 'Publicar en otra cuenta no esta habilitado para este usuario.'
      : quotaLimit > 0 && quotaRemaining <= 0
        ? `Te quedaste sin copias gratis. Haz upgrade de cuenta para seguir.`
        : quotaLimit > 0 && Math.max(copyCount, totalToCreate) > quotaRemaining
          ? `La selección necesita ${Math.max(copyCount, totalToCreate)} copias y te quedan ${quotaRemaining}. Haz upgrade de cuenta o reduce el alcance.`
          : '';
  const canContinueFromPreview = selectedPreviewListings.length > 0;
  const canContinueFromDestination = Boolean(selectedDestinationAccount);
  const jobDone = Number(jobSnapshot?.done || 0);
  const jobTotal = Number(jobSnapshot?.totalToCreate || totalToCreate || 0);
  const jobPercent = jobTotal > 0 ? Math.min(100, Math.round((jobDone / jobTotal) * 100)) : 0;
  const jobStatus = String(jobSnapshot?.status || '').toUpperCase();
  const jobRunning = jobStatus === 'RUNNING';
  const jobCompleted = jobStatus === 'COMPLETED' || jobStatus === 'COMPLETED_WITH_ERRORS';
  const jobReviewRequired = Number(jobSnapshot?.summary?.reviewRequired || 0);
  const jobErrorsCount = Number(jobSnapshot?.summary?.errors || 0);
  const jobHasErrors =
    jobStatus === 'COMPLETED_WITH_ERRORS' ||
    jobErrorsCount > 0 ||
    jobReviewRequired > 0;
  const packCopyNotices = useMemo(
    () =>
      Array.from(
        new Set(
          (jobSnapshot?.results || [])
            .filter((row) => row?.copyNotice?.type === 'PACK')
            .map((row) => Number(row.copyNotice.sourceSaleUnitQuantity))
            .filter((quantity) => Number.isSafeInteger(quantity) && quantity > 1),
        ),
      ).sort((left, right) => left - right),
    [jobSnapshot],
  );
  const hasKitCopyNotice = useMemo(
    () => (jobSnapshot?.results || []).some((row) => row?.copyNotice?.type === 'KIT_LINKED'),
    [jobSnapshot],
  );
  const hasArchivedKitCopyNotice = useMemo(
    () => (jobSnapshot?.results || []).some((row) => row?.copyNotice?.type === 'KIT'),
    [jobSnapshot],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    if (Array.isArray(preloadedDestinationAccounts)) {
      setDestinationAccounts(preloadedDestinationAccounts);
      setDestinationLoading(preloadedDestinationAccountsLoading);
      return undefined;
    }

    let alive = true;
    setDestinationLoading(true);
    setAnalysisError('');

    stockyApi
      .getSellers(authUser)
      .then((response) => {
        if (!alive) return;
        setDestinationAccounts(Array.isArray(response?.data) ? response.data : []);
      })
      .catch(() => {
        if (!alive) return;
        setDestinationAccounts([]);
      })
      .finally(() => {
        if (alive) setDestinationLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [open, authUser, preloadedDestinationAccounts, preloadedDestinationAccountsLoading]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const hasExistingJob = Boolean(resumeJobId && jobSnapshot);
    if (hasExistingJob) {
      setStep(STEP_JOB);
      return;
    }

    let alive = true;
    setStep(STEP_ANALYZING);
    setAnalysis(null);
    setAnalysisError('');
    setJobError('');
    setJobDetailOpen(false);
    setOptions(initialOptions);
    setSelectedChannel('mercado_libre');
    setSelectedListingIds([]);
    setCopyPreview(null);
    setCopyPreviewError('');
    setSelectedDestinationByChannel({
      mercado_libre: '',
      tienda_nube: '',
    });

    (async () => {
      try {
        setAnalysisLoading(true);
        const result = await analyzeSelectedSkus(selectedSkuIds, selectedRows);
        if (!alive) return;
        setAnalysis(result);
        setSelectedListingIds(
          Array.isArray(result?.previewListings)
            ? result.previewListings.map((listing) => listing.listingId).filter(Boolean)
            : [],
        );
        setStep(STEP_PREVIEW);
      } catch (error) {
        if (!alive) return;
        setAnalysisError(error?.message || 'No se pudieron analizar las publicaciones seleccionadas.');
        setStep(STEP_PREVIEW);
      } finally {
        if (alive) setAnalysisLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, resumeJobId, jobSnapshot, selectedRows, selectedSkuIds]);

  const setSelectedAccountsForChannel = (channel, accountId, checked) => {
    setCopyPreview(null);
    setCopyPreviewError('');
    setSelectedDestinationByChannel((prev) => {
      const nextValue = checked ? accountId : '';
      return {
        ...prev,
        [channel]: nextValue,
      };
    });
  };

  const handleChannelChange = (channel) => {
    setCopyPreview(null);
    setCopyPreviewError('');
    setSelectedChannel(channel);
  };

  const loadCopyPreview = async () => {
    if (!selectedDestinationAccount || selectedPreviewListings.length === 0) {
      return;
    }

    try {
      setCopyPreviewLoading(true);
      setCopyPreviewError('');
      const response = await stockyApi.previewListingCopy(authUser, {
        destination: { marketplace: 'tienda_nube', sellerId: selectedDestinationAccount.id },
        sourceListingIds: selectedPreviewListings.map((listing) => listing.listingId).filter(Boolean),
        options: buildTiendaNubeOptionsPayload(options),
      });
      setCopyPreview(response?.data || null);
    } catch (error) {
      setCopyPreview(null);
      setCopyPreviewError(
        error?.response?.data?.message ||
          error?.message ||
          'No pudimos preparar la vista previa de la copia.',
      );
    } finally {
      setCopyPreviewLoading(false);
    }
  };

  const goToConfirmStep = () => {
    setStep(STEP_CONFIRM);
    if (isTiendaNubeDestination) {
      void loadCopyPreview();
    }
  };

  const handleOptionChange = (name, checked) => {
    setOptions((prev) => ({ ...prev, [name]: checked }));
  };

  const handleListingToggle = (listingId, checked) => {
    setSelectedListingIds((prev) => {
      const current = new Set(prev);
      if (checked) {
        current.add(listingId);
      } else {
        current.delete(listingId);
      }
      return Array.from(current);
    });
  };

  const handleStartJob = async () => {
    if (!analysis || selectedPreviewListings.length === 0 || !selectedDestinationAccount) {
      return;
    }
    if (quotaBlocked) {
      setJobError(quotaBlockingMessage);
      return;
    }
    if (isTiendaNubeDestination && copyableListings.length === 0) {
      setJobError('Ninguna de las publicaciones seleccionadas se puede copiar a Tienda Nube.');
      return;
    }

    try {
      setJobError('');
      const payload = {
        authUser,
        selectedRows,
        analysis,
        selectedChannel,
        selectedListings: isTiendaNubeDestination ? copyableListings : selectedPreviewListings,
        selectedAccounts: [selectedDestinationAccount],
        options,
      };
      const job = await createPublishJob(payload);
      if (typeof onJobCreated === 'function') {
        onJobCreated(job);
      }
      setStep(STEP_JOB);
      setJobDetailOpen(true);
    } catch (error) {
      setJobError(error?.message || 'No se pudo iniciar el job de publicaciones.');
    }
  };

  const openCorrectionModal = (row) => {
    setCorrectionTarget(row || null);
    setCorrectionTitle('');
    setCorrectionError('');
    setCorrectionModalOpen(true);
  };

  const closeCorrectionModal = () => {
    if (correctionSaving) {
      return;
    }
    setCorrectionModalOpen(false);
    setCorrectionTarget(null);
    setCorrectionTitle('');
    setCorrectionError('');
  };

  const handleDrawerClose = () => {
    if (correctionModalOpen) {
      return;
    }
    if (jobRunning) {
      setJobError('No cierres esta ventana ni recargues la página hasta que finalice la publicación.');
      return;
    }
    onClose();
  };

  const selectedCorrectionMaxLength = Number(correctionTarget?.correction?.maxTitleLength || 0);
  const correctionTitleText = String(correctionTitle || '');
  const normalizedCorrectionTitle = correctionTitleText.trim();
  const correctionTitleLength = correctionTitleText.length;
  const canSaveCorrection =
    correctionSaving === false &&
    normalizedCorrectionTitle.length > 0 &&
    (selectedCorrectionMaxLength <= 0 || correctionTitleLength <= selectedCorrectionMaxLength);

  const resolveRetryErrorMessage = (error) => {
    const status = Number(error?.response?.status || 0);
    if (status === 404) {
      return 'El backend desplegado no reconoce el reintento de publicación. Desplegá el backend antes de volver a intentar.';
    }
    return error?.response?.data?.message || error?.message || 'No se pudo reintentar la publicación.';
  };

  const handleSaveCorrection = async () => {
    if (correctionSaving) {
      return;
    }

    if (!jobSnapshot?.id) {
      const message = 'No se encontró el job de publicación. Volvé a iniciar el flujo para reintentar.';
      setCorrectionError(message);
      setJobError(message);
      return;
    }
    if (!correctionTarget) {
      const message = 'No se encontró la publicación a corregir. Abrí el detalle del job y volvé a intentarlo.';
      setCorrectionError(message);
      setJobError(message);
      return;
    }
    if (!canSaveCorrection) {
      const message =
        selectedCorrectionMaxLength > 0 && correctionTitleLength > selectedCorrectionMaxLength
          ? `El título supera el máximo permitido de ${selectedCorrectionMaxLength} caracteres.`
          : 'Ingresa un título válido para reintentar.';
      setCorrectionError(message);
      setJobError(message);
      return;
    }

    const publicationId =
      correctionTarget.publicationId ||
      correctionTarget.marketplaceId ||
      correctionTarget.listingId ||
      correctionTarget.id;
    if (!publicationId) {
      const message = 'No se encontró el identificador de la publicación a reintentar.';
      setCorrectionError(message);
      setJobError(message);
      return;
    }

    try {
      setCorrectionSaving(true);
      setCorrectionError('');
      const updatedJob = await retryPublicationInJob({
        jobId: jobSnapshot.id,
        publicationId,
        title: normalizedCorrectionTitle,
        user: authUser,
        retryContext: {
          publishRequestPayload: correctionTarget.publishRequestPayload,
          sourcePublication: correctionTarget.sourcePublication,
          sourceRow: correctionTarget.sourceRow,
          destinationAccount: correctionTarget.destinationAccount,
          options,
        },
        jobSnapshot,
      });

      if (typeof onJobSnapshotChange === 'function') {
        onJobSnapshotChange(updatedJob);
      }

      setCorrectionModalOpen(false);
      setCorrectionTarget(null);
      setCorrectionTitle('');
      setCorrectionError('');
    } catch (error) {
      if (jobSnapshot?.id && typeof onJobSnapshotChange === 'function') {
        const refreshedJob = await getPublishJobStatus(jobSnapshot.id);
        if (refreshedJob) {
          onJobSnapshotChange(refreshedJob);
        }
      }
      const message = resolveRetryErrorMessage(error);
      setCorrectionError(message);
      setJobError(message);
    } finally {
      setCorrectionSaving(false);
    }
  };

  const renderStepHeader = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.05, color: '#111827' }}>
          Publicar en otra cuenta
        </div>
        <div style={{ marginTop: 4, color: '#6B7280' }}>{stepTitle(step)}</div>
      </div>
      <Button
        icon
        basic
        onClick={handleDrawerClose}
        aria-label="Cerrar drawer"
        title="Cerrar"
        style={{ flexShrink: 0 }}
      >
        <Icon name="close" />
      </Button>
    </div>
  );

  const renderLoadingPanel = ({ title, description, meta, rows = [] }) => (
    <div
      style={{
        border: '1px solid #E5E7EB',
        borderRadius: 14,
        background: '#FFFFFF',
        padding: 18,
        display: 'grid',
        gap: 18,
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div
          aria-hidden="true"
          style={{
            width: 48,
            height: 48,
            borderRadius: 14,
            background: '#F5F3FF',
            display: 'grid',
            placeItems: 'center',
            flex: '0 0 48px',
          }}
        >
          <Loader active inline size="small" />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.25, color: '#111827' }}>{title}</div>
          <div style={{ color: '#6B7280', lineHeight: 1.45, marginTop: 4 }}>{description}</div>
          {meta ? (
            <Label basic color="violet" style={{ marginTop: 10 }}>
              {meta}
            </Label>
          ) : null}
        </div>
      </div>
      {rows.length > 0 ? (
        <div style={{ display: 'grid', gap: 10 }}>
          {rows.map((row, index) => (
            <div
              key={`${row}-${index}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                borderRadius: 10,
                background: '#F8FAFC',
                color: '#4B5563',
              }}
            >
              <Icon name={index === 0 ? 'sync alternate' : 'circle notched'} loading={index === 0} color="violet" />
              <span>{row}</span>
            </div>
          ))}
        </div>
      ) : null}
      <Progress percent={100} active indicating color="violet" style={{ margin: 0 }} />
    </div>
  );

  const renderAnalyzingStep = () => (
    <Segment style={{ ...surfaceCardStyle, padding: 18, display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 14,
            background: '#F5F3FF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="sync alternate" color="violet" />
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>Analizando publicaciones...</div>
          <div style={{ color: '#6B7280' }}>
            Revisando los {selectedRows.length} SKUs seleccionados
          </div>
        </div>
      </div>
      {analysisLoading ? (
        renderLoadingPanel({
          title: 'Preparando el preview',
          description: 'Estamos reuniendo las publicaciones asociadas y dejando listas las opciones de copia.',
          meta: `${selectedRows.length} SKUs seleccionados`,
          rows: [
            'Agrupando publicaciones por SKU',
            'Validando datos de origen',
            'Armando la seleccion inicial',
          ],
        })
      ) : null}
      {analysisError ? <Message negative content={analysisError} /> : null}
    </Segment>
  );

  const renderPreviewStep = () => {
    const rows = analysis?.previewListings || [];
    const omittedCount = Number(analysis?.withoutPublicationsCount || 0);

    return (
      <Segment style={{ ...surfaceCardStyle, padding: 18, display: 'grid', gap: 16, minHeight: 0 }}>
        <div style={{ display: 'grid', gap: 6 }}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>Preview de listings</div>
          <div style={{ color: '#6B7280' }}>
            Revisa cada publicación antes de elegir el destino.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
            <Label color="violet">{selectedPreviewListings.length} seleccionadas</Label>
            <Label basic>{analysis?.skuCount || 0} SKUs</Label>
            {omittedCount > 0 ? <Label color="yellow">{omittedCount} SKUs sin publicaciones</Label> : null}
          </div>
        </div>
        <div style={{ maxHeight: 420, overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: 12 }}>
          {rows.length === 0 ? (
            <Message info style={{ margin: 0 }} content="No hay listings seleccionados todavía." />
          ) : (
            <Table celled striped compact="very" style={{ margin: 0 }}>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell style={{ width: 56 }} />
                  <Table.HeaderCell>Título</Table.HeaderCell>
                  <Table.HeaderCell>SKU</Table.HeaderCell>
                  <Table.HeaderCell>Catálogo</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((row) => {
                  const checked = selectedListingIds.includes(row.listingId);
                  return (
                    <Table.Row
                      key={`${row.rowId}-${row.listingId}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleListingToggle(row.listingId, !checked)}
                    >
                      <Table.Cell
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      >
                        <Checkbox
                          checked={checked}
                          onChange={(_, data) => handleListingToggle(row.listingId, Boolean(data.checked))}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <div style={{ fontWeight: 700 }}>{row.title || '-'}</div>
                        <div style={{ color: '#6B7280', marginTop: 4 }}>{row.accountName || 'Cuenta'}</div>
                      </Table.Cell>
                      <Table.Cell>{row.sku || '-'}</Table.Cell>
                      <Table.Cell>
                        <Label color={row.catalogListing ? 'teal' : 'grey'}>
                          {row.catalogListing ? 'Catálogo' : 'No catálogo'}
                        </Label>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          )}
        </div>
        {quotaLimit > 0 ? (
          <Message
            warning
            style={{ marginBottom: 0 }}
            content={`Te quedan ${quotaRemaining} copias gratis.`}
          />
        ) : null}
      </Segment>
    );
  };

  const renderDestinationStep = () => {
    const activeAccounts = currentDestinationAccounts;
    const selectedAccountId = selectedDestinationByChannel[selectedChannel] || '';
    return (
      <Segment style={{ ...surfaceCardStyle, padding: 18, display: 'grid', gap: 16 }}>
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>Elegir destino</div>
          <div style={{ color: '#6B7280' }}>
            Selecciona una sola cuenta destino según el canal.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {channelTabs.map((channel) => (
            <Button
              key={channel.key}
              type="button"
              basic={selectedChannel !== channel.key}
              color={selectedChannel === channel.key ? 'violet' : undefined}
              onClick={() => handleChannelChange(channel.key)}
            >
              {channel.label}
            </Button>
          ))}
        </div>

        {destinationLoading
          ? renderLoadingPanel({
              title: 'Buscando cuentas destino',
              description: 'Estamos cargando las cuentas conectadas disponibles para este canal.',
              meta: resolvePublicationLabel(selectedChannel),
              rows: [
                'Leyendo cuentas conectadas',
                'Filtrando por canal',
                'Preparando la seleccion de destino',
              ],
            })
          : null}

        {!destinationLoading && activeAccounts.length === 0 ? (
          <Message info content={`No hay cuentas destino de ${resolvePublicationLabel(selectedChannel)} disponibles.`} />
        ) : !destinationLoading ? (
          <div style={{ display: 'grid', gap: 6 }}>
            {activeAccounts.map((account) => (
              <Segment
                key={account.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedAccountsForChannel(selectedChannel, account.id, true)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelectedAccountsForChannel(selectedChannel, account.id, true);
                  }
                }}
                style={{
                  ...surfaceCardStyle,
                  padding: '8px 10px',
                  margin: 0,
                  border: selectedAccountId === account.id ? '1px solid #7C3AED' : '1px solid #E5E7EB',
                  boxShadow: 'none',
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <img
                      src={channelIcon(selectedChannel)}
                      alt={selectedChannel}
                      style={{ width: 20, height: 20, flex: '0 0 20px' }}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          lineHeight: 1.25,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {account.name}
                      </div>
                      <div style={{ color: '#6B7280', fontSize: 12, lineHeight: 1.25 }}>{account.statusLabel}</div>
                    </div>
                  </div>
                  <Checkbox
                    radio
                    checked={selectedAccountId === account.id}
                    onChange={(_, data) => setSelectedAccountsForChannel(selectedChannel, account.id, Boolean(data.checked))}
                  />
                </div>
              </Segment>
            ))}
          </div>
        ) : null}

        {!destinationLoading ? (
          <Message
            info
            style={{ marginBottom: 0 }}
            content={`Se crearán ${copyCount * (selectedAccountId ? 1 : 0)} publicaciones`}
          />
        ) : null}
        {quotaLimit > 0 && totalToCreate > quotaRemaining ? (
          <Message
            negative
            style={{ marginBottom: 0 }}
            content={`Con esta selección se necesitan ${totalToCreate} copias y solo te quedan ${quotaRemaining}. Haz upgrade de cuenta para continuar.`}
          />
        ) : null}
      </Segment>
    );
  };

  const renderTiendaNubeOptions = () => (
    <>
      <div>
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 6 }}>Estado del producto</div>
        <div style={{ display: 'grid', gap: 10 }}>
          <Checkbox
            radio
            label="Publicado en la tienda"
            checked={options.publishState !== 'draft'}
            onChange={() => handleOptionChange('publishState', 'published')}
          />
          <Checkbox
            radio
            label="Borrador (no visible para tus clientes)"
            checked={options.publishState === 'draft'}
            onChange={() => handleOptionChange('publishState', 'draft')}
          />
        </div>
      </div>

      <div>
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 6 }}>Precio</div>
        <div style={{ display: 'grid', gap: 10 }}>
          <Form.Input
            type="number"
            label="Ajuste sobre el precio de Mercado Libre (%)"
            value={options.priceAdjustmentPercent}
            min={-90}
            max={900}
            onChange={(_, data) => {
              const parsed = Number(data.value);
              handleOptionChange('priceAdjustmentPercent', Number.isFinite(parsed) ? parsed : 0);
            }}
          />
          <Checkbox
            label="Copiar precio promocional"
            checked={options.copyPromotionalPrice !== false}
            onChange={(_, data) => handleOptionChange('copyPromotionalPrice', Boolean(data.checked))}
          />
        </div>
      </div>

      <div>
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 6 }}>Contenido</div>
        <div style={{ display: 'grid', gap: 10 }}>
          <Checkbox
            label="Copiar imágenes"
            checked={options.copyImages !== false}
            onChange={(_, data) => handleOptionChange('copyImages', Boolean(data.checked))}
          />
          <Checkbox
            label="Copiar descripción"
            checked={options.copyDescription !== false}
            onChange={(_, data) => handleOptionChange('copyDescription', Boolean(data.checked))}
          />
          <Checkbox
            label="Agregar la ficha técnica de Mercado Libre al final de la descripción"
            checked={options.appendSpecsToDescription !== false}
            onChange={(_, data) =>
              handleOptionChange('appendSpecsToDescription', Boolean(data.checked))
            }
          />
          <Checkbox
            label="Marcar envío gratis si la publicación de origen lo tiene"
            checked={options.copyFreeShipping === true}
            onChange={(_, data) => handleOptionChange('copyFreeShipping', Boolean(data.checked))}
          />
        </div>
      </div>

      <div>
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 6 }}>Stock inicial</div>
        <div style={{ display: 'grid', gap: 10 }}>
          <Checkbox
            radio
            label="Usar el stock de MercuryCom"
            checked={options.stockSource !== 'zero'}
            onChange={() => handleOptionChange('stockSource', 'mercury')}
          />
          <Checkbox
            radio
            label="Crear con stock 0 para revisar antes de vender"
            checked={options.stockSource === 'zero'}
            onChange={() => handleOptionChange('stockSource', 'zero')}
          />
        </div>
      </div>
    </>
  );

  const renderMercadoLibreOptions = () => (
    <div>
      <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 6 }}>Copiar</div>
      <div style={{ display: 'grid', gap: 10 }}>
        {[
          ['copyPrice', 'Precio'],
          ['copyImages', 'Imagenes'],
          ['copyDescription', 'Descripcion'],
          ['copyAttributes', 'Atributos'],
          ['copyVariants', 'Variantes'],
        ].map(([key, label]) => (
          <Checkbox
            key={key}
            label={label}
            checked={options[key]}
            onChange={(_, data) => handleOptionChange(key, Boolean(data.checked))}
          />
        ))}
      </div>
    </div>
  );

  const renderOptionsStep = () => (
    <Segment style={{ ...surfaceCardStyle, padding: 18, display: 'grid', gap: 18 }}>
      {isTiendaNubeDestination ? renderTiendaNubeOptions() : renderMercadoLibreOptions()}
    </Segment>
  );

  const renderCopyPreviewProduct = (product) => {
    const blocking = Array.isArray(product?.blocking) ? product.blocking : [];
    const warnings = Array.isArray(product?.warnings) ? product.warnings : [];
    const variants = Array.isArray(product?.preview?.variants) ? product.preview.variants : [];
    const firstVariant = variants[0];

    return (
      <Segment
        key={product?.sourceRef?.productReference || product?.title}
        style={{
          ...surfaceCardStyle,
          padding: 12,
          margin: 0,
          boxShadow: 'none',
          border: blocking.length > 0 ? '1px solid #F3B6B6' : '1px solid #E5E7EB',
        }}
      >
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 700, minWidth: 0 }}>{product?.title || 'Publicación'}</div>
            {blocking.length > 0 ? (
              <Label color="red">No se puede copiar</Label>
            ) : (
              <Label color="green">Listo para copiar</Label>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', color: '#6B7280' }}>
            <Label basic>{product?.variantCount || 0} variantes</Label>
            <Label basic>{product?.preview?.imageCount || 0} imágenes</Label>
            {firstVariant ? <Label basic>Desde ${firstVariant.price}</Label> : null}
            {firstVariant ? <Label basic>Stock {firstVariant.stock}</Label> : null}
          </div>
          {product?.existsInDestination?.found ? (
            <Message
              warning
              style={{ margin: 0 }}
              content={`Ya existe un producto con el SKU ${product.existsInDestination.matchedSku} en la tienda: lo vamos a saltear.`}
            />
          ) : null}
          {product?.copyNotice?.type === 'KIT_LINKED' ? (
            <Message
              warning
              style={{ margin: 0 }}
              content="Este kit se copiará como kit y compartirá el stock y la composición con el original."
            />
          ) : null}
          {product?.copyNotice?.type === 'KIT' ? (
            <Message
              warning
              style={{ margin: 0 }}
              content="Este kit está dado de baja y se copiará como un producto simple. La copia no conservará la composición del kit."
            />
          ) : null}
          {blocking.map((issue) => (
            <Message key={issue.code} negative style={{ margin: 0 }} content={issue.message} />
          ))}
          {warnings.length > 0 ? (
            <ul style={{ margin: 0, paddingLeft: 18, color: '#6B7280' }}>
              {warnings.map((issue) => (
                <li key={issue.code}>{issue.message}</li>
              ))}
            </ul>
          ) : null}
        </div>
      </Segment>
    );
  };

  const renderConfirmStep = () => (
    <Segment style={{ ...surfaceCardStyle, padding: 18, display: 'grid', gap: 14 }}>
      <div>
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 4 }}>Confirmar</div>
        <div style={{ color: '#6B7280' }}>Revisa el origen y el destino antes de iniciar el job.</div>
      </div>
      <div style={{ display: 'grid', gap: 10 }}>
        <Label basic>Origen: {copyCount} publicaciones</Label>
        <Label basic>
          Destino: {resolvePublicationLabel(selectedChannel)} - {selectedDestinationAccount?.name || 'Sin seleccionar'}
        </Label>
        <Label color="violet">
          Resultado: {isTiendaNubeDestination ? copyableCount : totalToCreate} publicaciones nuevas
        </Label>
        {quotaBlocked ? (
          <Message warning style={{ marginBottom: 0 }} content={quotaBlockingMessage} />
        ) : null}
      </div>

      {isTiendaNubeDestination ? (
        <div style={{ display: 'grid', gap: 10 }}>
          {copyPreviewLoading
            ? renderLoadingPanel({
                title: 'Preparando la vista previa',
                description: 'Estamos leyendo las publicaciones en Mercado Libre y armando el producto para tu tienda.',
                meta: `${copyCount} publicaciones`,
                rows: ['Leyendo origen en Mercado Libre', 'Armando variantes y precios', 'Validando la tienda destino'],
              })
            : null}
          {copyPreviewError ? <Message negative style={{ margin: 0 }} content={copyPreviewError} /> : null}
          {!copyPreviewLoading && copyPreview?.products?.length > 0 ? (
            <div style={{ display: 'grid', gap: 10, maxHeight: 360, overflow: 'auto' }}>
              {copyPreview.products.map((product) => renderCopyPreviewProduct(product))}
            </div>
          ) : null}
          {!copyPreviewLoading && copyPreview && copyableCount === 0 ? (
            <Message
              negative
              style={{ margin: 0 }}
              content="Ninguna de las publicaciones seleccionadas se puede copiar a Tienda Nube."
            />
          ) : null}
        </div>
      ) : null}
    </Segment>
  );

  /**
   * La celda de motivo se mantiene compacta: el texto se recorta a dos líneas y
   * los avisos se consultan en un desplegable, para que una copia con varias
   * observaciones no estire la fila.
   */
  const renderResultReason = (row) => {
    const warnings = Array.isArray(row?.warnings) ? row.warnings : [];
    const text =
      row?.status === 'needs_review'
        ? row?.correction?.reason || row?.reason || row?.error || ''
        : row?.status === 'retrying'
          ? 'Reintentando la publicación.'
          : row?.reason || row?.error || '';

    if (!text && warnings.length === 0) {
      return <span style={{ color: '#9CA3AF' }}>-</span>;
    }

    return (
      <div style={{ display: 'grid', gap: 6, justifyItems: 'start' }}>
        {text ? (
          <Popup
            on="click"
            position="top center"
            wide="very"
            disabled={text.length <= 90}
            trigger={
              <div
                title={text.length > 90 ? 'Ver detalle' : undefined}
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  cursor: text.length > 90 ? 'pointer' : 'default',
                  lineHeight: 1.35,
                }}
              >
                {text}
              </div>
            }
            content={<div style={{ lineHeight: 1.45 }}>{text}</div>}
          />
        ) : null}
        {warnings.length > 0 ? (
          <Popup
            on="click"
            position="top center"
            wide="very"
            trigger={
              <Label basic color="yellow" size="tiny" style={{ cursor: 'pointer' }}>
                <Icon name="info circle" />
                {warnings.length} {warnings.length === 1 ? 'aviso' : 'avisos'}
              </Label>
            }
            content={
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.45 }}>
                {warnings.map((warning, warningIndex) => (
                  <li key={warning?.code || warningIndex}>{warning?.message}</li>
                ))}
              </ul>
            }
          />
        ) : null}
      </div>
    );
  };

  const renderJobStep = () => (
    <Segment style={{ ...surfaceCardStyle, padding: 18, display: 'grid', gap: 16 }}>
      <div style={{ display: 'grid', gap: 4 }}>
        <div style={{ fontWeight: 800, fontSize: 18 }}>
          {jobRunning ? 'Creando publicaciones...' : jobCompleted ? 'Publicaciones creadas' : 'Creando publicaciones...'}
        </div>
        <div style={{ color: '#6B7280' }}>{jobDone} / {jobTotal}</div>
      </div>
      <Progress
        percent={jobPercent}
        indicating={jobRunning}
        success={jobCompleted && !jobHasErrors}
        color={jobCompleted && jobHasErrors ? 'orange' : 'violet'}
        style={{ marginBottom: 0 }}
      />
      {jobRunning ? (
        <Message
          negative
          style={{
            marginBottom: 0,
            padding: 18,
            borderRadius: 12,
            border: '1px solid #F3B6B6',
            boxShadow: '0 8px 22px rgba(185, 28, 28, 0.08)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
            <div
              aria-hidden="true"
              style={{
                width: 42,
                height: 42,
                borderRadius: '50%',
                flex: '0 0 42px',
                display: 'grid',
                placeItems: 'center',
                background: '#FDE8E8',
                color: '#B91C1C',
              }}
            >
              <Icon name="warning sign" size="large" style={{ margin: 0 }} />
            </div>
            <div style={{ flex: '1 1 260px', minWidth: 0 }}>
              <Message.Header style={{ fontSize: 18, lineHeight: 1.3, marginBottom: 6 }}>
                No cierres esta ventana ni recargues la página
              </Message.Header>
              <div style={{ fontSize: 15, lineHeight: 1.5, color: '#7F1D1D' }}>
                Estamos creando las publicaciones. Salir de esta pantalla interrumpe el proceso actual.
              </div>
            </div>
          </div>
        </Message>
      ) : null}
      {jobCompleted && jobHasErrors ? (
        <Message
          warning
          style={{ marginBottom: 0 }}
          content={
            jobReviewRequired > 0
              ? 'Hubo publicaciones que requieren revisión. Revisalas en el detalle.'
              : 'Hubo publicaciones con error. Revisalas en el detalle.'
          }
        />
      ) : null}
      {packCopyNotices.map((quantity) => (
        <Message key={`pack-copy-notice-${quantity}`} warning style={{ marginBottom: 0 }}>
          Esta copia se creó como publicación simple. Configura las unidades por venta (pack x{quantity})
          en la copia, como hiciste con la original.
        </Message>
      ))}
      {hasKitCopyNotice ? (
        <Message warning style={{ marginBottom: 0 }}>
          Esta copia se creó como kit y comparte el stock con el kit original.
        </Message>
      ) : null}
      {hasArchivedKitCopyNotice ? (
        <Message warning style={{ marginBottom: 0 }}>
          Esta copia se creó como publicación simple porque el kit original está dado de baja.
        </Message>
      ) : null}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button basic onClick={() => setJobDetailOpen((prev) => !prev)}>
          Ver progreso completo
        </Button>
        {jobRunning ? null : null}
      </div>
      {jobError ? <Message negative content={jobError} /> : null}
      {jobSnapshot?.results?.length > 0 && jobDetailOpen ? (
        <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: 12 }}>
          <Table celled striped compact="very" style={{ margin: 0 }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>SKU</Table.HeaderCell>
                <Table.HeaderCell>Destino</Table.HeaderCell>
                <Table.HeaderCell>Estado</Table.HeaderCell>
                <Table.HeaderCell>Motivo</Table.HeaderCell>
                <Table.HeaderCell>Acción</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {(jobSnapshot.results || []).map((row, index) => (
                <Table.Row key={`${row.sku}-${index}`}>
                  <Table.Cell>{row.sku || '-'}</Table.Cell>
                  <Table.Cell>{row.destinationAccount?.name || '-'}</Table.Cell>
                  <Table.Cell>
                    {row.status === 'needs_review' ? (
                      <Label color="orange">
                        <Icon name="warning sign" />
                        Requiere revisión
                      </Label>
                    ) : row.status === 'retrying' ? (
                      <Label color="blue">
                        <Icon loading name="spinner" />
                        Reintentando...
                      </Label>
                    ) : row.status === 'created' ? (
                      <Label color="green">{row.resultLabel || 'Creada'}</Label>
                    ) : row.status === 'omitted' ? (
                      <Label color="grey">Ya existía</Label>
                    ) : (
                      <Label color={statusTone(row.status)}>{row.status}</Label>
                    )}
                  </Table.Cell>
                  <Table.Cell>{renderResultReason(row)}</Table.Cell>
                  <Table.Cell>
                    {row.reviewUrl && row.status !== 'needs_review' && row.status !== 'retrying' ? (
                      <Button
                        as="a"
                        href={row.reviewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="mini"
                        basic
                        color="violet"
                      >
                        Ver publicación
                      </Button>
                    ) : row.status === 'needs_review' ? (
                      <Button
                        size="mini"
                        basic
                        color="orange"
                        onClick={() => openCorrectionModal(row)}
                        disabled={correctionSaving}
                      >
                        Corregir
                      </Button>
                    ) : row.status === 'retrying' ? (
                      <Label basic color="blue">
                        Reintentando...
                      </Label>
                    ) : (
                      <span style={{ color: '#9CA3AF' }}>-</span>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      ) : null}
    </Segment>
  );

  const renderCurrentStep = () => {
    if (step === STEP_ANALYZING) return renderAnalyzingStep();
    if (step === STEP_PREVIEW) return renderPreviewStep();
    if (step === STEP_DESTINATION) return renderDestinationStep();
    if (step === STEP_OPTIONS) return renderOptionsStep();
    if (step === STEP_CONFIRM) return renderConfirmStep();
    return renderJobStep();
  };

  const drawerStyle = {
    position: 'fixed',
    top: 0,
    right: 0,
    height: '100vh',
    width: 'min(580px, calc(100vw - 24px))',
    background: '#F8FAFC',
    borderLeft: '1px solid #E5E7EB',
    boxShadow: '-24px 0 70px rgba(15, 23, 42, 0.18)',
    transform: open ? 'translateX(0)' : 'translateX(100%)',
    transition: 'transform 220ms ease',
    zIndex: 1200,
    display: 'flex',
    flexDirection: 'column',
  };

  return (
    <>
      {open ? (
        <div
          role="presentation"
          onClick={handleDrawerClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.28)',
            zIndex: 1190,
          }}
        />
      ) : null}
      <aside aria-hidden={!open} style={drawerStyle}>
        <div style={{ padding: 20, borderBottom: '1px solid #E5E7EB', background: '#FFFFFF' }}>
          {renderStepHeader()}
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16, overflow: 'auto', flex: 1 }}>
          {analysisError ? <Message negative content={analysisError} /> : null}
          {jobError ? <Message negative content={jobError} /> : null}
          {quotaBlocked ? <Message warning content={quotaBlockingMessage} /> : null}
          {renderCurrentStep()}
        </div>
        <div
          style={{
            padding: 20,
            borderTop: '1px solid #E5E7EB',
            background: '#FFFFFF',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginLeft: 'auto' }}>
            {step === STEP_PREVIEW ? (
              <>
                <Button basic onClick={handleDrawerClose}>Cerrar</Button>
                <Button
                  color="violet"
                  disabled={!canContinueFromPreview || quotaBlocked}
                  onClick={() => {
                    setStep(STEP_DESTINATION);
                  }}
                >
                  Continuar
                </Button>
              </>
            ) : null}
            {step === STEP_DESTINATION ? (
              <>
                <Button
                  basic
                  onClick={() => {
                    setStep(STEP_PREVIEW);
                  }}
                >
                  Atrás
                </Button>
                <Button
                  color="violet"
                  disabled={!canContinueFromDestination || quotaBlocked}
                  onClick={() => setStep(STEP_OPTIONS)}
                >
                  Continuar
                </Button>
              </>
            ) : null}
            {step === STEP_OPTIONS ? (
              <>
                <Button basic onClick={() => setStep(STEP_DESTINATION)}>Atrás</Button>
                <Button color="violet" onClick={goToConfirmStep}>
                  Continuar
                </Button>
              </>
            ) : null}
            {step === STEP_CONFIRM ? (
              <>
                <Button basic onClick={() => setStep(STEP_OPTIONS)}>Atrás</Button>
                <Button
                  color="violet"
                  disabled={
                    quotaBlocked ||
                    (isTiendaNubeDestination && (copyPreviewLoading || copyableCount === 0))
                  }
                  onClick={handleStartJob}
                >
                  Crear publicaciones
                </Button>
              </>
            ) : null}
            {step === STEP_JOB ? (
              <>
                <Button basic onClick={handleDrawerClose}>Cerrar</Button>
              </>
            ) : null}
          </div>
        </div>
      </aside>
      <Modal
        open={correctionModalOpen}
        onClose={closeCorrectionModal}
        size="small"
        closeIcon
        closeOnDimmerClick={false}
        dimmer={{ style: { zIndex: 1300 } }}
        style={{ zIndex: 1310 }}
      >
        <Modal.Header>Corregir publicación</Modal.Header>
        <Modal.Content>
          <div style={{ display: 'grid', gap: 14 }}>
            <div style={{ display: 'grid', gap: 6 }}>
              <Label basic>SKU: {correctionTarget?.sku || '-'}</Label>
              <Label basic>Canal: {resolvePublicationLabel(correctionTarget?.channel || selectedChannel)}</Label>
              <Label basic>Cuenta destino: {correctionTarget?.destinationAccount?.name || '-'}</Label>
              <Label basic>Categoría ML: {correctionTarget?.sourcePublication?.categoryId || '-'}</Label>
            </div>
            <Message warning style={{ marginBottom: 0 }}>
              <Message.Header>Problema detectado</Message.Header>
              <p style={{ marginBottom: 6 }}>{correctionTarget?.correction?.message || 'El título supera el máximo permitido para esta categoría.'}</p>
              <p style={{ marginBottom: 0 }}>
                Máximo permitido: {selectedCorrectionMaxLength > 0 ? `${selectedCorrectionMaxLength} caracteres` : 'No disponible'}
              </p>
            </Message>
            <Form>
              <Form.Field>
                <label>Título original</label>
                <Form.TextArea
                  readOnly
                  value={correctionTarget?.sourcePublication?.title || correctionTarget?.title || ''}
                  rows={3}
                />
                <div style={{ marginTop: 6, color: '#6B7280' }}>
                  {String(correctionTarget?.sourcePublication?.title || correctionTarget?.title || '').length}
                  {selectedCorrectionMaxLength > 0 ? ` / ${selectedCorrectionMaxLength}` : ''}
                </div>
              </Form.Field>
              <Form.Field required>
                <label>Nuevo título</label>
                <Form.Input
                  placeholder="Ingresa un nuevo título"
                  value={correctionTitle}
                  onChange={(event) => setCorrectionTitle(event.target.value)}
                  error={Boolean(correctionTitleLength > selectedCorrectionMaxLength && selectedCorrectionMaxLength > 0)}
                />
                <div
                  style={{
                    marginTop: 6,
                    color:
                      correctionTitleLength > selectedCorrectionMaxLength && selectedCorrectionMaxLength > 0
                        ? '#B91C1C'
                        : '#6B7280',
                  }}
                >
                  {correctionTitleLength}
                  {selectedCorrectionMaxLength > 0 ? ` / ${selectedCorrectionMaxLength}` : ''}
                </div>
                {correctionTitleLength > selectedCorrectionMaxLength && selectedCorrectionMaxLength > 0 ? (
                  <div style={{ marginTop: 6, color: '#B91C1C' }}>El título supera el máximo permitido.</div>
                ) : null}
              </Form.Field>
              {correctionError ? <Message negative content={correctionError} /> : null}
            </Form>
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={closeCorrectionModal} disabled={correctionSaving}>
            Cancelar
          </Button>
          <Button color="violet" onClick={handleSaveCorrection} disabled={!canSaveCorrection} loading={correctionSaving}>
            Guardar y reintentar
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
}

export default PublishInAnotherAccountDrawer;

